'use client'

import * as React from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { motion } from 'framer-motion'
import { useRouter, usePathname } from '@/i18n/navigation'
import { routing } from '@/i18n/routing'
import { cn } from '@/lib/utils'

interface LanguageSwitcherProps {
  className?: string
  variant?: 'default' | 'inverted'
}

const SEGMENT_WIDTH = 40 // px — must match the button width

export function LanguageSwitcher({
  className,
  variant = 'default',
}: LanguageSwitcherProps) {
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations('langSwitch')
  const [isPending, startTransition] = React.useTransition()

  // Optimistic active locale — updates instantly on click so the indicator
  // never visually disappears during the page transition.
  const [optimistic, setOptimistic] = React.useState<string>(locale)
  React.useEffect(() => {
    setOptimistic(locale)
  }, [locale])

  const activeLocale = isPending ? optimistic : locale
  const activeIndex = Math.max(0, routing.locales.indexOf(activeLocale as never))

  const switchTo = (next: string) => {
    if (next === activeLocale || isPending) return
    setOptimistic(next)
    startTransition(() => {
      router.replace(pathname, { locale: next })
    })
  }

  const isInverted = variant === 'inverted'

  return (
    <div
      role="group"
      aria-label={t('label')}
      className={cn(
        'relative isolate inline-flex items-center rounded-full p-[3px] backdrop-blur-md',
        'transition-colors duration-300',
        isInverted
          ? 'bg-white/10 ring-1 ring-white/20'
          : 'bg-text-primary/[0.06] ring-1 ring-text-primary/10',
        className,
      )}
    >
      {/* Sliding indicator — always rendered, never unmounted */}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute top-[3px] left-[3px] h-7 rounded-full"
        style={{
          width: SEGMENT_WIDTH,
          zIndex: 0,
          background: isInverted
            ? '#ffffff'
            : 'linear-gradient(135deg, #2B5AFF, #7C2FE8)',
          boxShadow: isInverted
            ? '0 2px 10px -2px rgba(255,255,255,0.4)'
            : '0 4px 14px -4px rgba(43,90,255,0.55)',
        }}
        initial={false}
        animate={{ x: activeIndex * SEGMENT_WIDTH }}
        transition={{ type: 'spring', stiffness: 520, damping: 38 }}
      />

      {routing.locales.map((code) => {
        const active = code === activeLocale
        const labelLong = code === 'en' ? t('english') : t('serbian')

        return (
          <button
            key={code}
            type="button"
            onClick={() => switchTo(code)}
            aria-label={labelLong}
            aria-pressed={active}
            style={{ width: SEGMENT_WIDTH, zIndex: 1 }}
            className={cn(
              'relative flex h-7 items-center justify-center rounded-full',
              'text-[11px] font-semibold uppercase tracking-[0.14em]',
              'transition-colors duration-300 outline-none',
              'focus-visible:ring-2 focus-visible:ring-blue/40',
              active
                ? isInverted
                  ? 'text-text-primary'
                  : 'text-white'
                : isInverted
                  ? 'text-white/65 hover:text-white'
                  : 'text-text-subtle hover:text-text-primary',
              isPending && !active && 'cursor-wait',
            )}
          >
            {t(`short.${code}`)}
          </button>
        )
      })}
    </div>
  )
}
